// Keeps the Rumio session JWT alive while the app sits in the host iframe.
//
// Host-issued iframe tokens live 300 s (apps contract §3.3). The host never
// re-navigates the iframe to refresh them; instead the app posts
// `rumio:session:refresh` to the parent and the host answers with
// `{ type: 'rumio:session', token }` (§3.4). We do that ahead of expiry, and
// on demand when the API answers 401 (see api.ts).
//
// Only messages whose source is our parent window are accepted: that
// window is the host, and nothing else can post as it.

import { getSessionToken, setSessionToken } from './session';

const REFRESH_TYPE = 'rumio:session:refresh';
const SESSION_TYPE = 'rumio:session';

/** Refresh this long before `exp` so in-flight requests never see a stale token. */
export const REFRESH_LEAD_MS = 60_000;
/** Give up waiting on the host after this long; callers treat it as "no token". */
export const REFRESH_TIMEOUT_MS = 5_000;

let listening = false;
let pending: Promise<string | null> | null = null;
let scheduleTimer: ReturnType<typeof setTimeout> | undefined;
const waiters = new Set<(token: string) => void>();

function isEmbedded(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

/** `exp` of a JWT in epoch ms, or null when the token can't be decoded. */
export function tokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function scheduleRefresh(token: string | null): void {
  clearTimeout(scheduleTimer);
  const exp = tokenExpiryMs(token);
  if (exp === null) return;
  const delay = Math.max(0, exp - REFRESH_LEAD_MS - Date.now());
  scheduleTimer = setTimeout(() => {
    void requestSessionRefresh();
  }, delay);
}

function onMessage(event: MessageEvent): void {
  if (event.source !== window.parent) return;
  const data = event.data as { type?: unknown; token?: unknown } | null;
  if (!data || data.type !== SESSION_TYPE || typeof data.token !== 'string') return;
  setSessionToken(data.token);
  scheduleRefresh(data.token);
  for (const resolve of waiters) resolve(data.token);
  waiters.clear();
}

function ensureListening(): void {
  if (listening) return;
  window.addEventListener('message', onMessage);
  listening = true;
}

/**
 * Ask the host for a fresh token. Concurrent callers share one request.
 * Resolves null when not embedded or when the host doesn't answer in time.
 */
export function requestSessionRefresh(): Promise<string | null> {
  if (!isEmbedded()) return Promise.resolve(null);
  if (pending) return pending;
  ensureListening();
  pending = new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => {
      waiters.delete(settle);
      pending = null;
      resolve(null);
    }, REFRESH_TIMEOUT_MS);
    function settle(token: string): void {
      clearTimeout(timer);
      pending = null;
      resolve(token);
    }
    waiters.add(settle);
    window.parent.postMessage({ type: REFRESH_TYPE }, '*');
  });
  return pending;
}

/** Start proactive refresh for the current token. Returns a cleanup fn. */
export function startSessionKeepAlive(): () => void {
  if (!isEmbedded()) return () => {};
  ensureListening();
  scheduleRefresh(getSessionToken());
  return () => {
    clearTimeout(scheduleTimer);
    window.removeEventListener('message', onMessage);
    listening = false;
  };
}

// For tests — drops listeners, timers and any in-flight request.
export function _resetSessionRefreshForTests(): void {
  clearTimeout(scheduleTimer);
  if (typeof window !== 'undefined') window.removeEventListener('message', onMessage);
  listening = false;
  pending = null;
  waiters.clear();
}
